import { useEffect, type FC, type PropsWithChildren } from 'react';

import { useRouter } from '@tanstack/react-router';

import { useMobileNavigationStore } from '@dcentralexpedientedigital/fe-shared/stores/mobile-navigation-store';
import { useNavigationStore } from '@dcentralexpedientedigital/fe-shared/stores/navigation-store';
import { makeClassesByStyles } from '@dcentralexpedientedigital/fe-ui/utils/classes';

export interface ISidebarProps {
    className?: string;
}
const cx = makeClassesByStyles();

export const Sidebar: FC<PropsWithChildren<ISidebarProps>> = ({ className, children }) => {
    const [isSideNavOpen] = useNavigationStore((state) => [state.isSideNavigationOpen]);

    const router = useRouter();

    const [closeMobileNavigation, isMobileNavigationOpen] = useMobileNavigationStore((state) => [
        state.closeMobileNavigation,
        state.isMobileNavigationOpen,
    ]);

    useEffect(() => {
        const handleSideMenuStateChange = (isOpen: boolean) => {
            const classes = ['overflow-hidden', 'lg:overflow-auto'];
            if (isOpen) {
                document.body.classList.add(...classes);
            } else {
                document.body.classList.remove(...classes);
            }
        };

        const unsubscribeOnLoad = router.subscribe('onResolved', closeMobileNavigation);
        // Lock body when side menu is open
        const unsubscribeStateChange = useMobileNavigationStore.subscribe((state) =>
            handleSideMenuStateChange(state.isMobileNavigationOpen),
        );

        return () => {
            unsubscribeOnLoad();
            unsubscribeStateChange();
        };
    }, [router]);

    return (
        <div
            className={cx(
                className,
                'lg:min-h-lvh min-h-[100dvh]',
                'z-side-menu',
                'flex flex-col w-full flex-none fixed lg:static transition-all lg:translate-x-0 bg-side-menu-bg lg:bg-side-menu-lg-bg',
                {
                    'lg:w-side-menu': isSideNavOpen,
                    'lg:w-side-menu-collapsed': !isSideNavOpen,
                    'translate-x-0': isMobileNavigationOpen,
                    '-translate-x-full': !isMobileNavigationOpen,
                },
            )}
        >
            <div
                className={cx(
                    'max-h-[100dvh] h-full flex flex-col flex-grow lg:max-h-lvh',
                    'lg:top-0',
                    'lg:sticky',
                    'overflow-y-auto',
                    'overscroll-contain',
                    'scroll-touch',
                )}
                style={{
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {children}
            </div>
        </div>
    );
};
